import _, { debounce, type DebounceSettings, DebouncedFunc } from 'lodash';
import { NavigationService, i18n, DateService } from 'services';
import Can from 'shared/permission/Can';
import enumTypes from 'constants/enumTypes';
import { selectLocaleResources } from 'app/locale/selectors';
import { selectResourceCurrenciesPrecisions, selectResourceSystemSettings } from 'app/resource/selectors/system';
import { AppConstants } from 'constants/index';
import { types, Permission } from 'constants/accessControl';
import qs from 'qs';
import { filterKeys } from 'constants/configKeys';
import { Permission as PermissionType } from 'app/resource/types/permission';
import { OddsFormat } from 'constants/enumTypes1';

/**
 * @param {array|null} configsColumns
 * @param {array} initialColumns
 * @param {boolean} isTranslated
 * @returns {array}
 */
import type { ColumnType } from 'antd/es/table';
import { store } from 'store';

/**
 *
 * @param  {function} componentImport
 * @returns {React.LazyExoticComponent<T>}
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Returns props for antd DatePicker/RangePicker that handle both min and max time constraints.
 * @param {Dayjs | null} minDate - The minimum selectable date and time.
 * @param {Dayjs | null} maxDate - The maximum selectable date and time.
 * @returns {Function} disabledTime
 */
import type { Dayjs } from 'dayjs';
import { ErrorMessage } from 'app/settings/types';
import { AppColumn, AppColumns } from 'types/table';
import { Permissions as PermissionsType } from 'shared/permission/types';
import { OrderType } from 'types';

import { decimalToAmerican } from './utils';

interface Column<T> extends Pick<AppColumn<T>, 'key' | 'title' | 'titleText' | 'dataIndex' | 'totalTitle'> {
  checked?: boolean;
  hide?: boolean;
}

type ErrorResponse = {
  response?: {
    status?: number;
    data?: unknown;
  };
};

export interface ExportColumn<T> extends ColumnType<T> {
  checked?: boolean;
  titleText?: string;
  totalTitle?: string;
}

type NumberFormatType = (typeof enumTypes.numberFormats)[keyof typeof enumTypes.numberFormats];
interface FilterConfigItem {
  checked?: boolean;
  visible?: boolean;
  [key: string]: unknown;
}
export type FilterConfigMap = Record<string, FilterConfigItem>;
type QueryParams = Record<string, unknown>;
type FilterKeys = (typeof filterKeys)[keyof typeof filterKeys];

type DisabledTimeResult = {
  disabledHours?: () => number[];
  disabledMinutes?: (hour: number) => number[];
};

type WeekRange = {
  start: Dayjs;
  end: Dayjs;
};

export type Debounced<T extends (...args: unknown[]) => unknown> = ((...args: Parameters<T>) => ReturnType<T>) & {
  cancel: () => void;
  flush: () => ReturnType<T>;
};

export type AnyMouseEvent = {
  button: number;
};

/**
 * Get notification message.
 * @param   {String}    String     The model type.
 * @param   {String}    String     The action type.
 * @param   {String}    String     The name of model.
 * @return  {String}    Return a generated message.
 */
export const getMessage = (model: string, action: string, name: string = ''): string => i18n.t('notification:generalMessage', { model, action, name });

/**
 * Get columns with additional props.
 * @returns {columns} Return columns with additional props.
 * @param columns
 */

export const getColumns = <T>(columns: Column<T>[]): Column<T>[] => columns.map((col) => ({
  ...col,
  onCell: (record: T) => ({
    record,
    ...col,
  }),
}));

/**
 * @param {number|undefined} partnerId
 * @returns {boolean}
 */
export const isOwnUser = (partnerId?: number): boolean => partnerId === enumTypes.systemPartnerId;

/**
 *
 * @returns {*}
 * @param columns
 * @param permissions
 * @param isViewMode
 */
export const getInitialColumns = <T>(columns: AppColumns<T>, permissions: Permission[] = [], isViewMode: boolean = false): AppColumns<T> => {
  const hasRestriction = permissions.some(
    ({ type, rule }: Permission) => (!isViewMode || (isViewMode && type === types.view))
      && !Can({
        type,
        rule,
        children: false,
        fallback: true,
      }),
  );

  // By extracting the logic, it's easier for TS to see both paths return C[]
  return hasRestriction ? columns : columns.filter((el) => el.key !== 'actions');
};

/**
 * @param {object} err
 * @param {boolean} includeErrorStatusCode
 * @returns {object}
 */

export const getError = (err: ErrorResponse, includeErrorStatusCode: boolean = false): ErrorMessage => {
  if ((err?.response?.status && err.response.status >= 500) || !err?.response?.data) {
    return {
      errors: [
        {
          errorMessage: i18n.t('notification:unknownError'),
          errorCode: 'unknownError',
        },
      ],
      status: includeErrorStatusCode && err?.response?.status ? err.response.status : null,
    };
  }
  if (err?.response?.status === 403) {
    return {
      errors: [
        {
          errorMessage: i18n.t('notification:permissionDenied'),
          errorCode: 'permissionDenied',
        },
      ],
      status: null,
    };
  }

  return {
    errors: Array.isArray(err.response.data) ? err.response.data : [err.response.data],
    status: includeErrorStatusCode && err?.response?.status ? err.response.status : null,
  };
};

/**
 * Navigation for report's actions.
 * @param {MouseEvent} e - Mouse down event.
 * @param {string} url - Navigate to url.
 */

export const handleNavigate = (e: AnyMouseEvent, url: string): void => {
  if (e.button === 1) {
    window.open(url, '_blank');
  } else if (!e.button) {
    NavigationService(url);
  }
};

/**
 * Construct exported columns based on user configuration and initial columns.
 * @param {Array<{ checked?: boolean; dataIndex?: string | number }>|null} configsColumns - User-configured columns with checked status.
 * @param {Array<ExportableColumn<T>>} initialColumns - Initial columns with exportable properties.
 * @param {boolean} isTranslated - Flag to determine if titles should be translated (default: false).
 * @returns {Array<{ title: string; dataIndex: string; totalTitle?: string }>} Array of columns to be exported with title, dataIndex, and optional totalTitle.
 */
// todo configsColumns is actually default, so we make it with ?, we must change order
export const getExportedColumns = <T>(
  configsColumns: { checked?: boolean; dataIndex?: string | number }[] | null = null,
  initialColumns: AppColumn<T>[],
  isTranslated: boolean = false,
): ExportColumn<T>[] => {
  const initialExportedColumns: Record<string, ExportColumn<T>> = initialColumns.reduce(
    (filtered, el) => {
      const dataIndex = el.dataIndex as string | undefined;

      if (el.title && dataIndex && dataIndex !== 'actions' && !el.hidden) {
        const title = el.titleText || el.title;
        return {
          ...filtered,
          [dataIndex]: {
            title: !isTranslated ? i18n.t(String(title)) : title,
            dataIndex,
            totalTitle: el.totalTitle ? i18n.t(el.totalTitle) : undefined,
          },
        };
      }

      return filtered;
    },
    {} as Record<string, ExportColumn<T>>,
  );

  if (configsColumns?.length) {
    return configsColumns
      .filter((el) => el.checked && el.dataIndex && el.dataIndex !== 'actions' && initialExportedColumns[el.dataIndex as string])
      .map((el) => initialExportedColumns[el.dataIndex as string]);
  }

  return Object.values(initialExportedColumns);
};

/**
 * @param permissions Array
 * @param isViewMode
 * @returns { number }
 */
export const getAllowedActionsCount = (permissions: Permission[], isViewMode = false): number => permissions.reduce((count, { type, rule }) => {
  if (isViewMode && type !== types.view) {
    return count;
  }

  if (
    !Can({
      type,
      rule,
      children: false,
      fallback: true,
    })
  ) {
    return count + 1;
  }

  return count;
}, 0);

/**
 * To keep query params in URL
 * @param {Record<string, unknown>} params - Query parameters object.
 * @param {string} key - Query key (default: 'inside').
 * @returns {string}
 */
export const getUrlSearchParams = (params: Record<string, unknown>, key: string = 'inside'): string => qs.stringify({ [key]: JSON.stringify(params) });

/**
 * @param {object} mapping
 * @returns { object } with translated values
 */
export const getMappingTranslation = <T extends Record<string, string>>(mapping: T): Record<keyof T, string> => Object.fromEntries(Object.entries(mapping).map(([key, value]) => [key, i18n.t(value)])) as Record<keyof T, string>;

/**
 *
 * @param {Number} val
 * @param {Number} decimal
 * @param {Boolean} isFormat
 * @param {Number} format
 * @param numberFormatFromState
 * @returns {Number} formatted string
 * e.g 1.234.567,89 , 1 234 567.89
 * * @returns {Number} string
 */

export const toFormatNumber = (val: number | string | null, decimal?: number, isFormat: boolean = true, format?: NumberFormatType): string => {
  if (Number.isNaN(Number(val)) || _.isNil(val) || val === '') {
    return '';
  }
  const numberFormat = format || selectResourceSystemSettings(store.getState())?.numberFormat || enumTypes.numberFormats.Space;

  const decimalSeparator = numberFormat === enumTypes.numberFormats.EU ? ',' : '.';

  const thousandSeparator = numberFormat === enumTypes.numberFormats.EU ? '.' : ' ';

  let value = val.toString();

  if (decimal !== undefined) {
    if (decimal === 0) {
      value = Math.trunc(Number(value)).toString();
    } else {
      const regex = new RegExp(`^-?\\d+(?:\\.\\d{0,${decimal}})?`);
      const match = value.match(regex);
      value = match ? match[0] : value;
    }
  }

  const parts = value.split('.');

  if (isFormat) {
    parts[0] = parts[0].replace(AppConstants.regex.splitNumber, `$1${thousandSeparator}`);
  }

  return parts.join(decimalSeparator);
};

/**
 *
 * @param val
 * @param currency string
 * @returns {Number} formatted string
 * @param isFormat boolean
 * e.g 1 0000 000 , 5000.00
 * * @returns {Number} string
 */
export const toFormatNumberByCurrency = <T extends string>(val: number | string | null, currency: T, isFormat = true): string => {
  const precision = selectResourceCurrenciesPrecisions(store.getState())[currency] ?? 2;
  return toFormatNumber(val, precision, isFormat);
};

/**
 * Get manage filters config by key
 * @param {object} baseFilterConfig - all filters form items names with properties
 * @param {string} filterKey - if exists consider userConfig as wll
 * @param {object} query - if key exists in it, then show form item
 * @returns {object} dataIndex:properties
 */
export const getFilterConfigs = (baseFilterConfig: FilterConfigMap, query?: QueryParams, filterKey?: FilterKeys): FilterConfigMap => {
  if (!baseFilterConfig || Object.keys(baseFilterConfig).length === 0) {
    return {};
  }

  const userFilterConfigs = filterKey && store.getState().userPreferences.configs?.[filterKey];

  return Object.keys(baseFilterConfig).reduce<FilterConfigMap>((acc, dataIndex) => {
    const baseItem = baseFilterConfig[dataIndex];

    const checked = userFilterConfigs && userFilterConfigs[dataIndex] !== undefined ? Boolean(userFilterConfigs[dataIndex]) : Boolean(baseItem?.checked);

    return {
      ...acc,
      [dataIndex]: {
        ...baseItem,
        checked,
        visible: query?.[dataIndex] !== undefined || checked,
      },
    };
  }, {});
};

/**
 * Convert dateTz Array to JSON string
 * @param {Array} dateArray.
 * @returns {string} stringifies JSON
 */
export const convertDateArrayToJSON = (dateArray: Array<Dayjs | null | undefined>): string => {
  const formattedDates = dateArray.map((el) => (el ? DateService.convertDateObjectToDateString(el) : null)).filter((date): date is string => Boolean(date));

  return JSON.stringify(formattedDates);
};

/**
 * Accepts an array of numbers and generates all possible sequences that can be formed from the elements of the array
 * @param {Array[number]} array
 * @returns {Array[strings|number]|*[]}
 */

export const generateRanges = (array: number[]): Array<number | string> => {
  if (array.length <= 1) {
    return [...array];
  }

  const sorted = [...array].sort((a, b) => a - b);

  const result: Array<number | string> = [];

  let start = 0;

  for (let i = 1; i <= sorted.length; i += 1) {
    if (sorted[i] !== sorted[i - 1] + 1) {
      if (start === i - 1) {
        result.push(sorted[start]);
      } else {
        result.push(`${sorted[start]}-${sorted[i - 1]}`);
      }
      start = i;
    }
  }

  return result;
};

/**
 *
 * @param {number} size by Byte
 * @returns {number} by MB
 */

export const getFileSizeByMb = (size: number): number => size / 1024 / 1024;

export const lazyWithRetry = (<T extends ComponentType>(importer: () => Promise<{ default: T }>): LazyExoticComponent<T> => {
  const chunkReloadKey = 'chunk-reload';
  return lazy(() => importer()
    .then((module) => {
      sessionStorage.removeItem(chunkReloadKey);
      return module;
    })
    .catch((error) => {
      if (typeof window === 'undefined') {
        throw error;
      }

      const hasRefreshed = sessionStorage.getItem(chunkReloadKey) === 'true';

      if (!hasRefreshed) {
        sessionStorage.setItem(chunkReloadKey, 'true');
        window.location.reload();
        return new Promise(() => {});
      }

      throw error;
    }));
}) as typeof lazy;

export function getDatePickerTimeLimitsProps(
  minDate: Dayjs | undefined | null,
  maxDate: Dayjs | undefined | null,
): {
  disabledTime: { (date: Dayjs | null): DisabledTimeResult };
} {
  return {
    disabledTime: (date: Dayjs | null): DisabledTimeResult => {
      if (!date) {
        return {};
      }

      let disabledHoursList: number[] = [];
      let disabledMinutesFn: (hour: number) => number[] = () => [];

      const isMin = !!minDate && date.isSame(minDate, 'day');

      const isMax = !!maxDate && date.isSame(maxDate, 'day');

      if (isMin && minDate) {
        disabledHoursList = [...disabledHoursList, ...Array.from({ length: minDate.hour() }, (_item, i) => i)];

        const prevFn = disabledMinutesFn;

        disabledMinutesFn = (hour: number) => (hour === minDate.hour() ? [...prevFn(hour), ...Array.from({ length: minDate.minute() }, (_item, i) => i)] : prevFn(hour));
      }

      if (isMax && maxDate) {
        disabledHoursList = [...disabledHoursList, ...Array.from({ length: 23 - maxDate.hour() }, (_item, i) => maxDate.hour() + 1 + i)];

        const prevFn = disabledMinutesFn;

        disabledMinutesFn = (hour: number) => (hour === maxDate.hour() ? [...prevFn(hour), ...Array.from({ length: 59 - maxDate.minute() }, (_item, i) => maxDate.minute() + 1 + i)] : prevFn(hour));
      }

      return {
        disabledHours: () => [...new Set(disabledHoursList)].sort((a, b) => a - b),
        disabledMinutes: disabledMinutesFn,
      };
    },
  };
}

/**
 * @param {String} str
 * @returns {String}
 */
export function convertCamelCaseToTitleCase(str: string): string {
  return str.replace(/([a-z])([A-Z]+)/g, (_match: string, lower: string, upper: string) => `${lower} ${upper}`).replace(/^./, (char: string) => char.toUpperCase());
}

/**
 * @param {dayjs.Dayjs | String | undefined} startDate
 * @param {dayjs.Dayjs | String | undefined} endDate
 * @returns Date range in days
 */
export function getDaysInRange(startDate: Dayjs | string | undefined, endDate: Dayjs | string | undefined): Array<{ date: Dayjs }> {
  if (!startDate || !endDate) {
    return [];
  }

  const result: Array<{ date: Dayjs }> = [];

  let current = DateService.getDateTz(startDate);
  const end = DateService.getDateTz(endDate);

  while (!current.isAfter(end)) {
    result.push({ date: current });
    current = current.add(1, 'day');
  }

  return result;
}

/**
 * @param {dayjs.Dayjs | String | undefined} startDateStr
 * @param {dayjs.Dayjs | String | undefined} endDateStr
 * @returns Date range in weeks
 */

export function getWeeksInRange(startDateStr: Dayjs | string | undefined, endDateStr: Dayjs | string | undefined): WeekRange[] {
  if (!startDateStr || !endDateStr) {
    return [];
  }

  const startDate = DateService.getDateTz(startDateStr);
  const endDate = DateService.getDateTz(endDateStr);

  const weekMap = new Map<string, WeekRange>();

  let current = startDate.startOf('day');

  while (!current.isAfter(endDate, 'day')) {
    const weekStart = current.startOf('week');
    const weekEnd = current.endOf('week');

    const adjustedWeekEnd = weekEnd.isAfter(endDate) ? endDate : weekEnd;

    const weekKey = `${DateService.getFormattedDate(weekStart)}-${DateService.getFormattedDate(adjustedWeekEnd)}`;

    weekMap.set(weekKey, {
      start: weekStart,
      end: adjustedWeekEnd,
    });

    current = current.add(1, 'day');
  }

  return Array.from(weekMap.values());
}

/**
 * Creates a debounced version of the given function.
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Delay in milliseconds (default: 500).
 * @param {Object} options - Lodash debounce options.
 * @returns {Function & { cancel: Function, flush: Function }} debounced function.
 */

export function createDebouncedCallback<Args extends unknown[], R>(func: (...args: Args) => R, delay = 500, options: DebounceSettings = {}): DebouncedFunc<(...args: Args) => R> {
  return debounce(func, delay, options);
}

/**
 * Query string to string with type check
 * @param v string
 * @returns value with ascend | descend type or undefined
 */
export const qsOrderType = (v: unknown): OrderType | undefined => (v === 'ascend' || v === 'descend' ? v : undefined);

/**
 * Checks whether a key is numeric-string (integer-like).
 * @param key key to test
 * @returns true if `key` is numeric-string, otherwise false
 */
const isNumericKey = (key: string): boolean => String(Number(key)) === key;

/**
 * Detects if a value looks like a compiled *numeric* TypeScript enum object.
 *
 * Numeric TS enums at runtime contain both:
 * - forward mapping: MemberName -> number
 * - reverse mapping: "number" -> MemberName
 *
 * @param map map to detect
 * @returns true when `map` appears enum-like (both directions exist)
 */
const isEnumLike = (map: Record<string, unknown>): boolean => {
  if (!map) {
    return false;
  }
  const entries = Object.entries(map);

  const hasForward = entries.some(([k, v]) => !isNumericKey(k) && typeof v === 'number');

  const hasReverse = entries.some(([k, v]) => isNumericKey(k) && typeof v === 'string');

  return hasForward && hasReverse;
};

/**
 * Normalizes an enum-like map so numeric TS enum objects become single-direction.
 *
 * If `map` is a numeric TS enum runtime object, this removes the reverse mapping
 *
 * @param map input map
 * @returns normalized map (forward-only) for numeric enums, or original map otherwise
 */
export const normalizeEnumLikeMap = <T extends Record<string, string | number>>(map: T): Record<keyof T, number> | T => {
  if (!isEnumLike(map)) return map;

  return Object.fromEntries(Object.entries(map).filter(([k]) => !isNumericKey(k))) as Record<keyof T, number>;
};

/**
 * Get translation by key
 * @param {string} enumName resource name
 * @param {string|number} key
 * @param {object| undefined} initialValues initial enum, is used when the translation call does not work
 * @returns {undefined|string}
 */
export const getResourceValue = (enumName: string, key: string | number, initialValues?: Record<string, string | number>) => selectLocaleResources(store.getState())?.[enumName]?.[key] ?? ((initialValues && _.invert(normalizeEnumLikeMap(initialValues))) || {})[key];

/**
 * Safely parse `inside` query json into typed object.
 * @param inside query "inside" value
 * @returns parsed object as TQuery or empty object on invalid input
 */
export const safeParseInside = <TQuery>(inside: unknown): TQuery => {
  if (typeof inside !== 'string' || !inside) return {} as TQuery;
  try {
    return JSON.parse(inside) as TQuery;
  } catch {
    return {} as TQuery;
  }
};

/**
 * Stringify `inside` query object and keep booleans while skipping empty values.
 * @param query inside query object
 * @returns serialized inside query string
 */
export const stringifyInside = <TQuery extends object>(query: TQuery): string => JSON.stringify(_.pickBy(query, (val) => _.identity(val) || _.isBoolean(val)));

/**
 * A function that organizes a list of permissions by resource category and name.
 *
 * @function
 * @param {PermissionType[]} [permissions] - An optional array of permissions to be grouped. Each permission should contain a resource
 *                                           with 'category' and 'name' properties.
 * @returns {PermissionsType} An object containing permissions grouped by their resource category and name. Each category
 *                            contains nested objects for resource names, which hold arrays of related permissions.
 */
export const groupPermissionsByResource = (permissions?: PermissionType[]): PermissionsType => {
  const result: PermissionsType = {};
  permissions?.forEach((el) => {
    result[el.resource.category] = result[el.resource.category] || {};
    result[el.resource.category][el.resource.name] = result[el.resource.category][el.resource.name] || [];
    result[el.resource.category][el.resource.name].push(el);
  });
  return result;
};

/**
 * Parses a comma-separated string of numbers into an array of numbers.
 *
 * @function
 * @name parseNumberList
 * @param {string} [value] - A comma-separated string of numeric values. If undefined or falsy, the function will return undefined.
 * @returns {number[] | undefined} An array of numbers parsed from the input string, or undefined if the input is not provided.
 */

export const parseNumberList = (value?: string): number[] | undefined => (value ? value.split(',').map(Number) : undefined);

/**
 * Converts an object to FormData, handling arrays and file/blob instances.
 * @param data - The object to be converted to FormData.
 * @returns A FormData instance containing the object's data.
 */
export function objectToFormData(data: Record<string, unknown>): FormData {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((val) => {
        if (val !== undefined && val !== null) {
          formData.append(key, val instanceof File || val instanceof Blob ? val : val.toString());
        }
      });
    } else if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
    } else {
      formData.append(key, value.toString());
    }
  });

  return formData;
}

type NumericEnumValue<TEnum extends Record<string, string | number>> = Extract<TEnum[keyof TEnum], number>;

/**
 * Parses a comma-separated string and returns an array of valid numeric enum values.
 * @param value - The comma-separated string to parse.
 * @param enumType - The enum type to validate against.
 * @returns An array of valid numeric enum values or undefined if none are found.
 */

export const parseNumericEnumList = <TEnum extends Record<string, string | number>>(value: string | undefined, enumType: TEnum): NumericEnumValue<TEnum>[] | undefined => {
  const enumValues = new Set(Object.values(normalizeEnumLikeMap(enumType)));

  return parseNumberList(value)?.filter((item): item is NumericEnumValue<TEnum> => enumValues.has(item));
};

/**
 * Parses a comma-separated string and returns the first valid numeric enum value.
 * @param value - The comma-separated string to parse.
 * @param enumType - The enum type to validate against.
 * @returns The first valid numeric enum value or undefined if none are found.
 */

export const parseNumericEnumValue = <TEnum extends Record<string, string | number>>(value: string | undefined, enumType: TEnum): NumericEnumValue<TEnum> | undefined => parseNumericEnumList(value, enumType)?.[0];

/**
 * Formats amount by currency precision.
 * Keep UI rendering (span/StyledCellAlign) in TSX files.
 */
export const renderAmount = (value: number | null, currencyId?: string | null): string | undefined => (!_.isNil(value) ? toFormatNumberByCurrency(value, currencyId as string) : undefined);

/**
 *
 * @param val number
 * @param oddsFormat OddsFormat
 */
export const formatOdds = (val: number | null, oddsFormat?: OddsFormat) => (oddsFormat === OddsFormat.American && val != null ? `${decimalToAmerican(val) || AppConstants.notAvailable} (${val})` : val);

/**
 * Renders the RTP (Return to Player).
 * @param record An object containing optional `rtpFrom` and `rtpTo` properties.
 * @returns A string representing the RTP range or a single value, or undefined if both values are missing.
 */
export const renderRTP = (record?: { rtpFrom?: number, rtpTo?: number }): string | undefined => {
  if (!record) return undefined;

  const { rtpFrom, rtpTo } = record;

  if (_.isNil(rtpFrom) && _.isNil(rtpTo)) return undefined;

  return rtpFrom === rtpTo
    ? `${rtpFrom}`
    : `${rtpFrom} - ${rtpTo}`;
};
